import { AuthResponse, LoginRequest, RegisterRequest, User } from '../types/auth';
import { Note } from '../types/notes';
import {
  Collection,
  CreateCollectionPayload,
  UpdateCollectionPayload,
} from '../types/collections';
import { StreamEvent, Thread, ThreadSummary } from '../types/threads';
import { parseSSEStream } from './sse';
import { Plan } from '../lib/billing';

const API_BASE_URL = process.env.REACT_APP_API_URL ?? 'https://paperbrain.dev';

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

const handleResponse = async (response: Response) => {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw { response: { data: error, status: response.status } };
  }
  return response.json();
};

export const authApi = {
  login: async (credentials: LoginRequest): Promise<AuthResponse> => {
    const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(credentials),
    });
    return handleResponse(response);
  },

  register: async (userData: RegisterRequest): Promise<AuthResponse> => {
    const response = await fetch(`${API_BASE_URL}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(userData),
    });
    return handleResponse(response);
  },

  getCurrentUser: async (): Promise<User> => {
    const response = await fetch(`${API_BASE_URL}/api/user/me`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  updateAccount: async (
    payload: UpdateAccountRequest,
  ): Promise<UpdateAccountResponse> => {
    const response = await fetch(`${API_BASE_URL}/api/user/me`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    return handleResponse(response);
  },
};

export interface UpdateAccountRequest {
  name?: string;
  email?: string;
  current_password?: string;
  new_password?: string;
}

export interface UpdateAccountResponse {
  user: User;
  access_token: string | null;
  token_type: string | null;
}

export const uploadApi = {
  uploadFile: async (file: File) => {
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await fetch(`${API_BASE_URL}/api/upload/`, {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: formData,
    });
    return handleResponse(response);
  },
};

export const notesApi = {
  getNote: async (noteId: number | string): Promise<Note> => {
    const response = await fetch(`${API_BASE_URL}/api/notes/${noteId}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  getUserNotes: async (inboxed?: boolean): Promise<Note[]> => {
    const params = new URLSearchParams();
    if (inboxed !== undefined) {
      params.append('inboxed', inboxed.toString());
    }
    const queryString = params.toString();
    const url = `${API_BASE_URL}/api/notes/${queryString ? `?${queryString}` : ''}`;
    
    const response = await fetch(url, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  updateNote: async (note: Note): Promise<Note> => {
    const response = await fetch(`${API_BASE_URL}/api/notes/${note.id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(note),
    });

    return handleResponse(response);
  },

  getNoteImageUrl: (noteId: number): string => {
    const token = localStorage.getItem('token');
    return `${API_BASE_URL}/api/notes/${noteId}/image?token=${encodeURIComponent(token || '')}`;
  },

  deleteNotes: async (noteIds: number[]): Promise<void> => {
    const response = await fetch(`${API_BASE_URL}/api/notes/delete`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ note_ids: noteIds }),
    });
    return handleResponse(response);
  },

  /** Re-queue a note whose OCR gave up, so a bad scan isn't a dead end. */
  retryNote: async (noteId: number): Promise<Note> => {
    const response = await fetch(`${API_BASE_URL}/api/notes/${noteId}/retry`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  deleteAnnotation: async ({
    noteId,
    annotationId,
  }: {
    noteId: number;
    annotationId: number;
  }): Promise<void> => {
    const response = await fetch(
      `${API_BASE_URL}/api/notes/${noteId}/annotations/${annotationId}`,
      { method: 'DELETE', headers: getAuthHeaders() },
    );
    // 204 has no body, so skip handleResponse's json() on success.
    if (!response.ok) await handleResponse(response);
  },

  getNoteCollection: async (noteId: number): Promise<{ note_id: number; collection_id: number | null }> => {
    const response = await fetch(`${API_BASE_URL}/api/notes/${noteId}/collection`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  setNoteCollection: async (
    noteId: number,
    collectionId: number | null,
  ): Promise<{ note_id: number; collection_id: number | null }> => {
    const response = await fetch(`${API_BASE_URL}/api/notes/${noteId}/collection`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ collection_id: collectionId }),
    });
    return handleResponse(response);
  },
};

export const collectionsApi = {
  list: async (): Promise<Collection[]> => {
    const response = await fetch(`${API_BASE_URL}/api/collections/`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  get: async (collectionId: number): Promise<Collection> => {
    const response = await fetch(`${API_BASE_URL}/api/collections/${collectionId}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  create: async (payload: CreateCollectionPayload): Promise<Collection> => {
    const response = await fetch(`${API_BASE_URL}/api/collections/`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    return handleResponse(response);
  },

  update: async (
    collectionId: number,
    payload: UpdateCollectionPayload,
  ): Promise<Collection> => {
    const response = await fetch(`${API_BASE_URL}/api/collections/${collectionId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    return handleResponse(response);
  },

  delete: async (collectionId: number): Promise<void> => {
    const response = await fetch(`${API_BASE_URL}/api/collections/${collectionId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Request failed' }));
      throw { response: { data: error, status: response.status } };
    }
  },
};

export const threadsApi = {
  list: async (): Promise<ThreadSummary[]> => {
    const response = await fetch(`${API_BASE_URL}/api/threads/`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  get: async (threadId: number): Promise<Thread> => {
    const response = await fetch(`${API_BASE_URL}/api/threads/${threadId}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  create: async (title?: string | null): Promise<Thread> => {
    const response = await fetch(`${API_BASE_URL}/api/threads/`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ title: title ?? null }),
    });
    return handleResponse(response);
  },

  delete: async (threadId: number): Promise<void> => {
    const response = await fetch(`${API_BASE_URL}/api/threads/${threadId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Request failed' }));
      throw { response: { data: error, status: response.status } };
    }
  },

  streamMessage: async function* (
    threadId: number,
    content: string,
  ): AsyncGenerator<StreamEvent> {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_BASE_URL}/api/threads/${threadId}/messages`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: JSON.stringify({ content }),
    });
    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ detail: 'Request failed' }));
      throw { response: { data: error, status: response.status } };
    }
    yield* parseSSEStream<StreamEvent>(response);
  },
};

export interface SubscriptionStatus {
  status: string | null;
  price_id: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  is_active: boolean;
  /** Which provider owns the subscription: 'paddle' (web) or 'revenuecat' (mobile). */
  provider?: string | null;
  /** POST /sync only: false when a provider couldn't be reached. */
  synced?: boolean | null;
}

export interface CheckoutResponse {
  /** A Paddle `txn_…` id for `Paddle.Checkout.open({ transactionId })`. */
  transaction_id: string;
}

export interface PortalResponse {
  portal_url: string;
}

export const paymentsApi = {
  getSubscription: async (): Promise<SubscriptionStatus> => {
    const response = await fetch(`${API_BASE_URL}/api/payments/subscription`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  /** What's on sale, read live from Paddle. Public — no auth needed. */
  getPlans: async (): Promise<Plan[]> => {
    const response = await fetch(`${API_BASE_URL}/api/payments/plans`);
    return handleResponse(response);
  },

  /** 409 with `code: 'payment_needs_update' | 'already_subscribed'` when buying would double-bill. */
  createCheckout: async (plan = 'monthly'): Promise<CheckoutResponse> => {
    const response = await fetch(`${API_BASE_URL}/api/payments/create-checkout`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ plan }),
    });
    return handleResponse(response);
  },

  createPortalSession: async (): Promise<PortalResponse> => {
    const response = await fetch(`${API_BASE_URL}/api/payments/create-portal-session`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  /**
   * Ask the backend to re-read the subscription from Paddle directly instead of
   * waiting on the webhook. Used right after checkout so the success page doesn't
   * have to poll, and as the recovery path for a dropped webhook.
   */
  sync: async (): Promise<SubscriptionStatus> => {
    const response = await fetch(`${API_BASE_URL}/api/payments/sync`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },
};
